import type Phaser from 'phaser';
import type { BiomeId } from '../../content/biomes';
import type { BossKind } from '../../gen/boss';

/**
 * Each zone's art outside the lawns: its neighborhood strip (the menu's zone banner, the Next Zone
 * page), its 9 x 9 world icon and its boss's badge (the zone strip, plates, trophies).
 */

/**
 * The zone's scene behind its plate: the world in a strip, in its own light (the Backyard's
 * houses, the Park's trees and lamps, the Farm's barn and silo at golden hour, the Golf Course's
 * fairways, the Cemetery at dusk, the Estate's manor, the Stadium under floodlights). Drawn into a
 * box the strip's height, so on short screens (an iPad's) a tall landmark is trimmed at the top
 * instead of reaching into the header.
 */
export function drawNeighborhood(
  scene: Phaser.Scene,
  top: number,
  h: number,
  biome: BiomeId = 'backyard',
): void {
  const { width } = scene.scale.gameSize;
  const g = scene.make.graphics({}, false);
  paintNeighborhood(g, width, h, biome);
  scene.add.renderTexture(0, top, width, h).setOrigin(0).draw(g).render();
  g.destroy();
}

function paintNeighborhood(
  g: Phaser.GameObjects.Graphics,
  width: number,
  h: number,
  biome: BiomeId,
): void {
  const top = 0;
  const ink = 0x10180c;
  const SKY: Record<BiomeId, [number, number]> = {
    backyard: [0x8fd3ff, 0xb5e3ff],
    park: [0x7cc8ff, 0xaee0ff],
    farm: [0xffb46a, 0xffd89a],
    golf: [0x8fd3ff, 0xc9ecff],
    cemetery: [0x3a2f6e, 0x6a5aa8],
    estate: [0xa9b8c8, 0xc8d3de],
    stadium: [0x1c2550, 0x34407a],
    meadow: [0x86cfff, 0xd8f2ff],
  };
  const [sky, haze] = SKY[biome];
  g.fillStyle(sky).fillRect(0, top, width, h);
  g.fillStyle(haze).fillRect(0, top, width, Math.round(h / 3));
  const base = top + h - 6;
  const ground = (c: number) => g.fillStyle(c).fillRect(0, base, width, 6);
  const tree = (x: number, r: number, c: number, lit: number) => {
    g.fillStyle(0x6a3f1f).fillRect(x - 1, base - 6, 3, 6);
    g.fillStyle(ink).fillCircle(x, base - 8 - r * 0.4, r + 1);
    g.fillStyle(c).fillCircle(x, base - 8 - r * 0.4, r);
    g.fillStyle(lit).fillCircle(x - r / 3, base - 9 - r * 0.6, r / 2);
  };
  switch (biome) {
    case 'park': {
      for (let x = 6; x < width; x += 28) tree(x, 8 + (x % 3), 0x2f8236, 0x4ea83f);
      for (let x = 20; x < width; x += 56) {
        g.fillStyle(ink).fillRect(x, base - 20, 2, 20);
        g.fillStyle(0xffd23f).fillRect(x - 2, base - 24, 6, 4);
      }
      ground(0x3f9a35);
      return;
    }
    case 'farm': {
      // Fields in rows, a red barn, a silo.
      for (let y = base - 10; y < base; y += 3)
        g.fillStyle(y % 2 ? 0xc49a3a : 0xb08a2e).fillRect(0, y, width, 3);
      const bx = width - 74;
      g.fillStyle(ink).fillRect(bx - 1, base - 25, 34, 26);
      g.fillStyle(0xc8303a).fillRect(bx, base - 18, 32, 18);
      g.fillStyle(0x5a5f6e).fillTriangle(bx - 2, base - 18, bx + 16, base - 30, bx + 34, base - 18);
      g.lineStyle(1, 0xfffaf0).strokeRect(bx + 11.5, base - 12.5, 9, 12);
      g.fillStyle(ink).fillRect(bx + 38, base - 32, 12, 32);
      g.fillStyle(0xbcc2d2).fillRect(bx + 39, base - 31, 10, 31);
      g.fillStyle(0x8a90a8).fillRect(bx + 39, base - 34, 10, 4);
      for (let x = 4; x < bx - 10; x += 36) tree(x, 7, 0x2f8236, 0x4ea83f);
      ground(0x4f8a2a);
      return;
    }
    case 'golf': {
      for (let y = base - 12; y < base; y += 4)
        g.fillStyle(y % 8 ? 0x5cb944 : 0x4ea83f).fillRect(0, y, width, 4);
      g.fillStyle(0xd9c590).fillEllipse(width * 0.3, base - 4, 26, 6);
      g.fillStyle(0xfffaf0).fillRect(width * 0.62, base - 24, 1, 18);
      g.fillStyle(0xff5b47).fillTriangle(
        width * 0.62 + 1,
        base - 24,
        width * 0.62 + 9,
        base - 21,
        width * 0.62 + 1,
        base - 18,
      );
      for (const x of [10, width - 20]) tree(x, 9, 0x2f8236, 0x4ea83f);
      ground(0x3f9a35);
      return;
    }
    case 'cemetery': {
      g.fillStyle(0xfff6b8).fillCircle(width - 26, top + 10, 5);
      g.fillStyle(0x4c4870).fillRect(0, base - 14, width, 8);
      for (let x = 0; x < width; x += 12) g.fillStyle(0x3a365a).fillRect(x, base - 14, 1, 8);
      for (let x = 8; x < width; x += 18) {
        g.fillStyle(ink).fillRect(x - 1, base - 11, 8, 11);
        g.fillStyle(0x9aa0b4).fillRect(x, base - 10, 6, 10);
        g.fillStyle(0xc6cbdc).fillRect(x, base - 10, 2, 10);
      }
      g.fillStyle(0x6a6480).fillRect(width / 2 - 1, base - 30, 2, 24);
      for (const [dx, dy] of [
        [-8, -24],
        [7, -27],
        [-5, -18],
        [6, -16],
      ] as const)
        g.lineStyle(1, 0x6a6480).lineBetween(width / 2, base - 20, width / 2 + dx, base + dy);
      ground(0x2f5a3a);
      return;
    }
    case 'estate': {
      const mx = width / 2 - 50;
      g.fillStyle(ink).fillRect(mx - 1, base - 27, 102, 28);
      g.fillStyle(0xe8e0cc).fillRect(mx, base - 26, 100, 26);
      g.fillStyle(0x8a90a8).fillRect(mx - 2, base - 30, 104, 4);
      for (let x = mx + 6; x < mx + 96; x += 12) {
        g.fillStyle(0x6fa0ff).fillRect(x, base - 21, 6, 9);
        g.fillStyle(0xfffaf0).fillRect(x + 2, base - 21, 1, 9);
      }
      g.fillStyle(0x9a5232).fillRect(width / 2 - 4, base - 12, 8, 12);
      for (const x of [4, width - 18]) {
        g.fillStyle(ink).fillRect(x - 1, base - 9, 16, 9);
        g.fillStyle(0x2f8236).fillRect(x, base - 8, 14, 8);
        g.fillStyle(0x86cf52).fillRect(x, base - 8, 14, 2);
      }
      ground(0x3f9a35);
      return;
    }
    case 'meadow': {
      // Rolling hills to the horizon, a grand tree on the near one, a split-rail fence.
      g.fillStyle(0x7cc06a).fillEllipse(width * 0.25, base + 4, width * 0.9, 26);
      g.fillStyle(0x6ab35a).fillEllipse(width * 0.8, base + 6, width * 0.8, 30);
      g.fillStyle(0x5aa64a).fillEllipse(width * 0.5, base + 10, width * 1.2, 22);
      tree(width * 0.72, 12, 0x2f8236, 0x5aae46);
      for (const [x, c] of [
        [14, 0xffffff],
        [30, 0xffe14a],
        [52, 0xd9a8ff],
        [width - 30, 0xffffff],
      ] as const)
        g.fillStyle(c).fillRect(x, base - 2, 2, 2);
      for (let x = 4; x < width; x += 22) g.fillStyle(0x8a6a3e).fillRect(x, base - 9, 2, 9);
      g.fillStyle(0xb08a5a).fillRect(0, base - 7, width, 1);
      g.fillStyle(0xb08a5a).fillRect(0, base - 4, width, 1);
      ground(0x4f9e3e);
      return;
    }
    case 'stadium': {
      // Banked stands full of fans, floodlight towers.
      for (let y = base - 18; y < base - 4; y += 3) {
        g.fillStyle(y % 2 ? 0x3a3550 : 0x332f48).fillRect(0, y, width, 3);
        for (let x = (y * 7) % 5; x < width; x += 4) {
          g.fillStyle([0xc8303a, 0x3a63d0, 0xffd23f, 0xfffaf0][(x + y) % 4]!).fillRect(
            x,
            y + 1,
            1,
            1,
          );
        }
      }
      for (const x of [10, width - 14]) {
        g.fillStyle(ink).fillRect(x, base - 34, 2, 30);
        g.fillStyle(0xfffaf0).fillRect(x - 4, base - 38, 10, 5);
        g.fillStyle(0xfff6b8, 0.3).fillTriangle(
          x - 4,
          base - 33,
          x + 6,
          base - 33,
          x + 1,
          base - 4,
        );
      }
      g.fillStyle(0x6a3a2e).fillRect(0, base - 4, width, 4);
      ground(0x3f9a35);
      return;
    }
    default: {
      const walls = [0xe8dcc0, 0xffd9a8, 0xd8e6ff, 0xf2c6c0];
      for (let i = 0, hx = -8; hx < width; i++, hx += 34) {
        g.fillStyle(ink).fillTriangle(hx - 1, base - 13, hx + 14, base - 24, hx + 29, base - 13);
        g.fillStyle(0xc8553d).fillTriangle(
          hx + 1,
          base - 14,
          hx + 14,
          base - 23,
          hx + 27,
          base - 14,
        );
        g.fillStyle(ink).fillRect(hx + 2, base - 14, 25, 15);
        g.fillStyle(walls[i % walls.length]!).fillRect(hx + 3, base - 13, 23, 13);
        g.fillStyle(0x6b4423).fillRect(hx + 12, base - 8, 5, 8);
      }
      ground(0x3f9a35);
    }
  }
}

/** A 9 x 9 badge marking a zone's boss lawn on its plate, one per kind of boss. */
export function drawBossBadge(
  g: Phaser.GameObjects.Graphics,
  kind: BossKind,
  x: number,
  y: number,
): void {
  switch (kind) {
    case 'storm':
      // A storm cloud with rain.
      g.fillStyle(0x10180c)
        .fillRect(x, y + 1, 9, 5)
        .fillRect(x + 2, y, 5, 1);
      g.fillStyle(0x8a96a8)
        .fillRect(x + 1, y + 2, 7, 3)
        .fillRect(x + 3, y + 1, 3, 1);
      g.fillStyle(0x7cc6ff)
        .fillRect(x + 2, y + 7, 1, 2)
        .fillRect(x + 5, y + 7, 1, 2);
      return;
    case 'art':
      // A prize rosette.
      g.fillStyle(0x10180c)
        .fillRect(x + 1, y, 7, 6)
        .fillRect(x, y + 1, 9, 4);
      g.fillStyle(0xd8432f)
        .fillRect(x + 2, y + 1, 5, 4)
        .fillRect(x + 1, y + 2, 7, 2);
      g.fillStyle(0xffd23f).fillRect(x + 3, y + 2, 3, 2);
      g.fillStyle(0xd8432f)
        .fillRect(x + 2, y + 6, 2, 3)
        .fillRect(x + 5, y + 6, 2, 3);
      return;
    case 'show':
      // A soccer ball (mower soccer).
      g.fillStyle(0x10180c)
        .fillRect(x + 2, y, 5, 9)
        .fillRect(x, y + 2, 9, 5)
        .fillRect(x + 1, y + 1, 7, 7);
      g.fillStyle(0xffffff)
        .fillRect(x + 2, y + 1, 5, 7)
        .fillRect(x + 1, y + 2, 7, 5);
      g.fillStyle(0x222326)
        .fillRect(x + 3, y + 3, 3, 3)
        .fillRect(x + 4, y + 1, 1, 1)
        .fillRect(x + 1, y + 5, 1, 1)
        .fillRect(x + 7, y + 5, 1, 1);
      return;
    case 'maze':
      // A little maze of hedges.
      g.fillStyle(0x10180c).fillRect(x, y, 9, 9);
      g.fillStyle(0x2f6d28).fillRect(x + 1, y + 1, 7, 7);
      g.fillStyle(0xd9e8a0)
        .fillRect(x + 2, y + 2, 5, 1)
        .fillRect(x + 2, y + 2, 1, 5)
        .fillRect(x + 4, y + 4, 3, 1)
        .fillRect(x + 6, y + 4, 1, 3)
        .fillRect(x + 2, y + 6, 3, 1);
      return;
    case 'meadow':
      // A daisy in the grass.
      g.fillStyle(0x10180c).fillRect(x, y, 9, 9);
      g.fillStyle(0x4f9e3e).fillRect(x + 1, y + 1, 7, 7);
      g.fillStyle(0xfffaf0)
        .fillRect(x + 3, y + 1, 3, 2)
        .fillRect(x + 3, y + 6, 3, 2)
        .fillRect(x + 1, y + 3, 2, 3)
        .fillRect(x + 6, y + 3, 2, 3);
      g.fillStyle(0xffd23f).fillRect(x + 3, y + 3, 3, 3);
      return;
    case 'dark':
      // An old lantern, lit.
      g.fillStyle(0x10180c)
        .fillRect(x + 2, y, 5, 1)
        .fillRect(x + 1, y + 1, 7, 7)
        .fillRect(x + 4, y + 8, 1, 1);
      g.fillStyle(0xf2c94c).fillRect(x + 2, y + 2, 5, 5);
      g.fillStyle(0xfff2a8).fillRect(x + 3, y + 3, 3, 2);
      return;
    case 'robo':
      // The robot mower's face: a visor with red eyes and an antenna.
      g.fillStyle(0x10180c)
        .fillRect(x, y + 2, 9, 7)
        .fillRect(x + 4, y, 1, 2);
      g.fillStyle(0xe8ecf0).fillRect(x + 1, y + 3, 7, 5);
      g.fillStyle(0x222326).fillRect(x + 2, y + 4, 5, 2);
      g.fillStyle(0xff5a4a)
        .fillRect(x + 2, y + 4, 1, 1)
        .fillRect(x + 6, y + 4, 1, 1);
      g.fillStyle(0xffd23f).fillRect(x + 4, y, 1, 1);
      return;
    case 'sheep':
      // A woolly sheep, face on.
      g.fillStyle(0x10180c)
        .fillRect(x, y, 9, 7)
        .fillRect(x + 2, y + 6, 5, 3);
      g.fillStyle(0xf1ece0).fillRect(x + 1, y + 1, 7, 5);
      g.fillStyle(0x3a3330).fillRect(x + 3, y + 3, 3, 5);
      g.fillStyle(0x3a3330)
        .fillRect(x, y + 3, 2, 1)
        .fillRect(x + 7, y + 3, 2, 1);
      g.fillStyle(0xffffff)
        .fillRect(x + 3, y + 4, 1, 1)
        .fillRect(x + 5, y + 4, 1, 1);
      return;
  }
}

/** The Backyard's house icon, 9 x 9: k ink, r roof, w wall, d door. */
const HOUSE_ICON = [
  '....k....',
  '...krk...',
  '..krrrk..',
  '.krrrrrk.',
  'kkkkkkkkk',
  '.kwwwwwk.',
  '.kwwdwwk.',
  '.kwwdwwk.',
  '.kkkkkkk.',
];

/** A rolling hill with a daisy on it, inside the 9 x 9 cell like the other zones' icons. */
const MEADOW_ICON = [
  '....w....',
  '...wyw...',
  '....w....',
  '....s....',
  '..kkskk..',
  '.kgggggk.',
  'kgggggggk',
  'kgggggggk',
  'kkkkkkkkk',
];

/** Draws a small pixel map at (x, y): one character per pixel, '.' left clear. */
function drawPixels(
  g: Phaser.GameObjects.Graphics,
  x: number,
  y: number,
  rows: readonly string[],
  colors: Readonly<Record<string, number>>,
): void {
  rows.forEach((row, dy) => {
    for (let dx = 0; dx < row.length; dx++) {
      const c = colors[row[dx]!];
      if (c !== undefined) g.fillStyle(c).fillRect(x + dx, y + dy, 1, 1);
    }
  });
}

/** A 9 x 9 icon per world, for the strip under the zone banner. */
export function drawWorldIcon(
  g: Phaser.GameObjects.Graphics,
  id: BiomeId,
  x: number,
  y: number,
): void {
  const ink = 0x10180c;
  switch (id) {
    case 'backyard':
      // Pixel by pixel (a filled triangle rasterizes lopsided this small): a symmetric roof, the
      // door in the middle.
      drawPixels(g, x, y, HOUSE_ICON, {
        k: ink,
        r: 0xc8553d,
        w: 0xffd9a8,
        d: 0x6b4423,
      });
      return;
    case 'park':
      g.fillStyle(ink).fillCircle(x + 4, y + 3, 4);
      g.fillStyle(0x3f9a35).fillCircle(x + 4, y + 3, 3);
      g.fillStyle(0x6b4423).fillRect(x + 4, y + 6, 1, 3);
      return;
    case 'farm':
      g.fillStyle(ink).fillRect(x, y + 1, 9, 8);
      g.fillStyle(0xc8553d).fillRect(x + 1, y + 2, 7, 6);
      g.fillStyle(0xf4f1de)
        .fillRect(x + 2, y + 3, 5, 1)
        .fillRect(x + 4, y + 3, 1, 5);
      return;
    case 'golf':
      g.fillStyle(0x7fcf68).fillRect(x, y + 7, 9, 2);
      g.fillStyle(0xf4f1de).fillRect(x + 3, y, 1, 8);
      g.fillStyle(0xe0533d).fillTriangle(x + 4, y, x + 8, y + 2, x + 4, y + 4);
      return;
    case 'cemetery':
      g.fillStyle(ink).fillRect(x + 1, y + 1, 7, 8);
      g.fillStyle(0x9aa0a6).fillRect(x + 2, y + 2, 5, 7);
      g.fillStyle(0x6f757b).fillRect(x + 3, y + 4, 3, 1);
      return;
    case 'estate':
      g.fillStyle(ink).fillRect(x, y + 1, 9, 8);
      g.fillStyle(0xf4f1de).fillRect(x + 1, y + 2, 7, 6);
      g.fillStyle(ink)
        .fillRect(x + 2, y + 3, 1, 5)
        .fillRect(x + 4, y + 3, 1, 5)
        .fillRect(x + 6, y + 3, 1, 5);
      return;
    case 'stadium':
      g.fillStyle(0xf4f1de)
        .fillRect(x + 1, y, 1, 9)
        .fillRect(x + 7, y, 1, 9)
        .fillRect(x + 1, y + 5, 7, 1);
      return;
    case 'meadow':
      // Pixel by pixel, so it keeps to its cell (an ellipse spilled below the bar).
      drawPixels(g, x, y, MEADOW_ICON, {
        k: ink,
        g: 0x5aae46,
        w: 0xfffaf0,
        y: 0xffd23f,
        s: 0x2f8236,
      });
      return;
  }
}

const TROPHY_ROWS = [
  '..GGGGGGGGGGG..',
  'GGGLLLLLLLLLGGG',
  'G.GLLLLLLLLLG.G',
  'G.GLGGGGGGGLG.G',
  'G.GLGGGGGGGLG.G',
  '.GGGGGGGGGGGGG.',
  '...GGGGGGGGG...',
  '....GGGGGGG....',
  '.....GGGGG.....',
  '......GGG......',
  '......DDD......',
  '......GGG......',
  '....DDDDDDD....',
  '...GGGGGGGGG...',
  '...GGGGGGGGG...',
];

/** A zone trophy's size in art pixels (it is drawn at a whole-number scale). */
export const TROPHY_SIZE = { w: TROPHY_ROWS[0]!.length, h: TROPHY_ROWS.length } as const;

/**
 * A zone trophy: a gold cup on a stand at (x, y), `k` screen pixels per art pixel, with a drop
 * shadow. Not won yet: a dark silhouette.
 */
export function drawTrophy(
  g: Phaser.GameObjects.Graphics,
  x: number,
  y: number,
  k: number,
  won: boolean,
): void {
  const colors: Record<string, number> = won
    ? { G: 0xffd23f, L: 0xfff1a8, D: 0xc07a00 }
    : { G: 0x2a2550, L: 0x352f66, D: 0x221d44 };
  const each = (fn: (rx: number, ry: number, c: string) => void) =>
    TROPHY_ROWS.forEach((row, ry) => [...row].forEach((c, rx) => c !== '.' && fn(rx, ry, c)));
  if (won) each((rx, ry) => g.fillStyle(0x0b0820).fillRect(x + rx * k + 2, y + ry * k + 2, k, k));
  each((rx, ry, c) => g.fillStyle(colors[c]!).fillRect(x + rx * k, y + ry * k, k, k));
}
